import type { WidgetService } from "../types";

/**
 * Client-side mirror of `public.service_capacity_status`, whose live
 * definition is
 * supabase/migrations/20260720000080_capacity_gate_current_model.sql
 * (superseding 20260516000001_capacity_publish_gate.sql and
 * 20260720000070_capacity_gate_enquiry_services.sql). When you change the
 * gate, change this file in the same PR — the original mirror silently
 * drifted for two revisions because nothing ties them together.
 *
 * Defense in depth — the database-level publish gate (trigger
 * `trg_widget_configs_capacity_gate`) is the authoritative enforcement.
 * This client check exists for the narrow window where a service's
 * capacity was removed AFTER its widget was already published; the gate
 * doesn't re-validate on every edit, so a misconfigured service can
 * theoretically reach a guest. Rather than letting them tap Book and
 * fail downstream, we render an "unavailable" state up-front.
 *
 * Errs on the side of "bookable" when the widget payload doesn't carry
 * enough information to determine unbookability (e.g. accommodation
 * rooms and floor-plan max_capacity aren't currently in the payload).
 */
export function isServiceBookable(service: WidgetService): boolean {
  const units = service.units ?? [];
  const plans = service.floor_plans ?? [];
  const specialDates = service.special_date_events ?? [];

  // Special-date events provide their own fixed-menu booking path with
  // their own party + price rules — always let the guest through.
  if (specialDates.length > 0) return true;

  switch (service.service_type) {
    case "accommodation":
      // accommodation_rooms aren't in the widget payload; server-side
      // gate handles the room check. We can only validate the unit cap
      // from here.
      return units.some((u) => (u.capacity ?? 0) > 0);

    case "dining": {
      const someMode =
        service.allow_general_reservation || service.allow_specific_reservation;
      if (!someMode) return false;

      if (service.allow_specific_reservation) {
        // Need at least one bookable seat with capacity > 0 (own or
        // inherited from its linked unit).
        const seatWithCapacity = plans.some((p) =>
          p.elements.some((e) => {
            if (e.kind !== "seat") return false;
            if ((e.capacity ?? 0) > 0) return true;
            const u = units.find((x) => x.id === e.service_unit_id);
            return (u?.capacity ?? 0) > 0;
          }),
        );
        if (!seatWithCapacity) return false;
      }

      if (service.allow_general_reservation) {
        // max_capacity isn't in the widget payload — proxy with "has at
        // least one floor plan OR at least one unit with capacity". The
        // server-side gate is authoritative for the strict ceiling check.
        const hasGeneralProxy =
          plans.length > 0 || units.some((u) => (u.capacity ?? 0) > 0);
        if (!hasGeneralProxy) return false;
      }
      return true;
    }

    case "spa": {
      // ChiDesk integration owns the capacity contract; ensure at least
      // one exposed, active treatment is reachable.
      const treatments = service.chidesk?.treatments ?? [];
      return treatments.length > 0;
    }

    case "events":
      // Per-event capacity is enforced on the event itself; here the
      // service is "bookable" if it surfaces any units to choose from.
      return units.length > 0;

    default:
      // picnic, tasting, tour, custom, club — at least one enabled unit
      // whose inventory is UNLIMITED (null, per the
      // `service_units.inventory_count` column contract) or positive, or
      // that carries a per-booking capacity. An explicit 0 stays
      // unbookable: it fails all three legs.
      return units.some(
        (u) =>
          u.inventory_count == null ||
          u.inventory_count > 0 ||
          (u.capacity ?? 0) > 0,
      );
  }
}
